import React, { useEffect, useMemo, useState } from "react";
import {
  fetchPrestations,
  fetchPrestationsKpis,
  createPrestation,
  updatePrestation,
  changePrestationStatus,
  createPaymentPlan,
  recordDirectPayment,
  fetchPrestation,
  generatePrestationQuote,
} from "../api/prestations.api";
import { fetchCustomers, createCustomerAPI } from "../api/customers.api";
import { fetchProjects, createProject, updateProject, deleteProject } from "../api/projects.api";
import {
  Briefcase,
  Users,
  DollarSign,
  Plus,
  Search,
  Calendar,
  SlidersHorizontal,
  AlertTriangle,
  CheckCircle2,
  FolderKanban,
  CreditCard,
  Wallet,
} from "lucide-react";
import {
  KpiCard,
  SkeletonGrid,
  formatMoneyXOF,
  parseTvaPct,
  parseMoneyToInt,
  buildInstallments,
  clampDate,
} from "./prestations/helpers.js";
import PrestationCard from "./prestations/PrestationCard.jsx";
import PrestationFormModal from "./prestations/PrestationFormModal.jsx";
import PaymentModal from "./prestations/PaymentModal.jsx";
import ProjectCard from "./prestations/ProjectCard.jsx";
import ProjectFormModal from "./prestations/ProjectFormModal.jsx";

export default function Prestations() {
  const [prestations, setPrestations] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showModal, setShowModal] = useState(false);
  const [editingPrestation, setEditingPrestation] = useState(null);

  const [searchDraft, setSearchDraft] = useState("");
  const [filters, setFilters] = useState({
    search: "",
    status: "",
    paymentMode: "",
    overdue: false,
    sortBy: "created_at",
    sortOrder: "desc",
  });

  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    customer_id: "",
    project_id: "",
    title: "",
    description: "",
    amount_ht: "",
    tva_pct: "18",
    payment_mode: "ONE_SHOT",
    status: "DRAFT",
    service_date: "",
    due_date: "",
  });

  // Client block (suggest + manual fields)
  const [customerSearch, setCustomerSearch] = useState("");
  const [pickedCustomer, setPickedCustomer] = useState(null);
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerAddress, setCustomerAddress] = useState("");

  // Installments (payment plan)
  const [installmentCount, setInstallmentCount] = useState(3);
  const [installmentFrequency, setInstallmentFrequency] = useState("monthly");
  const [installmentStartDate, setInstallmentStartDate] = useState("");
  const [installments, setInstallments] = useState([]);
  const [autoSplit, setAutoSplit] = useState(true);
  const [installmentsError, setInstallmentsError] = useState("");

  // KPIs from backend
  const [kpis, setKpis] = useState(null);

  // Payment modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentTarget, setPaymentTarget] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    amount: "",
    method: "cash",
    provider: "orange_money",
    reference: "",
    notes: "",
    paid_at: "",
  });
  const [savingPayment, setSavingPayment] = useState(false);
  const [changingStatusId, setChangingStatusId] = useState(null);
  const [openDetailId, setOpenDetailId] = useState(null);
  const [detailsById, setDetailsById] = useState({});
  const [loadingDetailId, setLoadingDetailId] = useState(null);
  const [generatingQuoteId, setGeneratingQuoteId] = useState(null);

  // ── Onglets ──
  const [activeTab, setActiveTab] = useState("prestations");

  // ── États Projets ──
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [projectSaving, setProjectSaving] = useState(false);
  const [deletingProjectId, setDeletingProjectId] = useState(null);
  const [projectSearchDraft, setProjectSearchDraft] = useState("");
  const [projectFilters, setProjectFilters] = useState({ search: "", status: "", sort: "updated_desc" });
  const [projectFormData, setProjectFormData] = useState({
    customer_id: "",
    title: "",
    description: "",
    status: "DRAFT",
    start_date: "",
    end_date: "",
  });

  const statusConfig = {
    DRAFT: { label: "Brouillon", tone: "neutral" },
    PENDING: { label: "En attente", tone: "yellow" },
    IN_PROGRESS: { label: "En cours", tone: "blue" },
    PARTIALLY_PAID: { label: "Partiellement payé", tone: "purple" },
    PAID: { label: "Payé", tone: "green" },
    CANCELLED: { label: "Annulé", tone: "red" },
  };

  const paymentModeConfig = {
    ONE_SHOT: { label: "Paiement unique", icon: Wallet },
    INSTALLMENTS: { label: "Échelonné", icon: CreditCard },
  };

  useEffect(() => {
    const t = setTimeout(() => {
      setFilters((f) => ({ ...f, search: searchDraft }));
    }, 300);
    return () => clearTimeout(t);
  }, [searchDraft]);

  useEffect(() => {
    const t = setTimeout(() => {
      setProjectFilters((f) => ({ ...f, search: projectSearchDraft }));
    }, 300);
    return () => clearTimeout(t);
  }, [projectSearchDraft]);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status, filters.search, filters.paymentMode, filters.sortBy, filters.sortOrder, filters.overdue]);

  async function loadData() {
    setLoading(true);
    try {
      const [prestationsData, customersData, projectsData, kpisData] = await Promise.all([
        fetchPrestations({
          search: filters.search,
          status: filters.status,
          paymentMode: filters.paymentMode,
          sortBy: filters.sortBy,
          sortOrder: filters.sortOrder,
          overdue: filters.overdue,
        }),
        fetchCustomers(),
        fetchProjects({}),
        fetchPrestationsKpis(),
      ]);
      setPrestations(Array.isArray(prestationsData) ? prestationsData : []);
      setCustomers(Array.isArray(customersData) ? customersData : []);
      setProjects(Array.isArray(projectsData) ? projectsData : []);
      setKpis(kpisData);
    } catch (err) {
      console.error("Erreur:", err);
      setPrestations([]);
    } finally {
      setLoading(false);
    }
  }

  function resetForm() {
    setFormData({
      customer_id: "",
      project_id: "",
      title: "",
      description: "",
      amount_ht: "",
      tva_pct: "18",
      payment_mode: "ONE_SHOT",
      status: "DRAFT",
      service_date: "",
      due_date: "",
    });
    setCustomerSearch("");
    setPickedCustomer(null);
    setCustomerName("");
    setCustomerPhone("");
    setCustomerEmail("");
    setCustomerAddress("");

    setInstallmentCount(3);
    setInstallmentFrequency("monthly");
    setInstallmentStartDate("");
    setInstallments([]);
    setAutoSplit(true);
    setInstallmentsError("");
  }

  function openCreate() {
    resetForm();
    setEditingPrestation(null);
    setShowModal(true);
  }

  function openEdit(prestation) {
    setEditingPrestation(prestation);
    setFormData({
      customer_id: prestation.customer_id,
      project_id: prestation.project_id || "",
      title: prestation.title || "",
      description: prestation.description || "",
      amount_ht: prestation.amount_ht || prestation.amount || "",
      tva_pct: prestation.tva_pct ?? "18",
      payment_mode: prestation.payment_mode || "ONE_SHOT",
      status: prestation.status || "DRAFT",
      service_date: clampDate(prestation.service_date),
      due_date: clampDate(prestation.due_date),
    });
    setCustomerSearch(prestation.customer_name || "");
    setPickedCustomer(
      prestation.customer_id
        ? {
            id: prestation.customer_id,
            uid: prestation.customer_uid,
            name: prestation.customer_name,
            phone: prestation.customer_phone,
            email: prestation.customer_email,
          }
        : null
    );
    setCustomerName(prestation.customer_name || "");
    setCustomerPhone(prestation.customer_phone || "");
    setCustomerEmail(prestation.customer_email || "");
    setCustomerAddress("");

    setInstallmentStartDate(clampDate(prestation.due_date) || clampDate(prestation.service_date) || "");
    setAutoSplit(true);
    setShowModal(true);
  }

  function openPaymentModal(prestation) {
    setPaymentTarget(prestation);
    setPaymentForm({
      amount: prestation.due_total || "",
      method: "cash",
      provider: "orange_money",
      reference: "",
      notes: "",
      paid_at: new Date().toISOString().slice(0, 10),
    });
    setShowPaymentModal(true);
  }

  async function toggleDetails(prestationId) {
    if (!prestationId) return;
    if (openDetailId === prestationId) {
      setOpenDetailId(null);
      return;
    }

    setOpenDetailId(prestationId);
    if (detailsById[prestationId]) return;

    setLoadingDetailId(prestationId);
    try {
      const detail = await fetchPrestation(prestationId);
      setDetailsById((prev) => ({ ...prev, [prestationId]: detail }));
    } catch (err) {
      console.error("Erreur chargement détail prestation:", err);
    } finally {
      setLoadingDetailId(null);
    }
  }

  async function handleGenerateQuote(prestation) {
    if (!prestation?.id) return;
    if (prestation.quote_id) {
      alert(`Un devis est déjà lié à cette prestation.`);
      return;
    }
    setGeneratingQuoteId(prestation.id);
    try {
      const result = await generatePrestationQuote(prestation.id);
      setPrestations((prev) =>
        prev.map((p) =>
          p.id === prestation.id
            ? { ...p, quote_id: result.quote_id, quote_external_id: result.external_id }
            : p
        )
      );
      alert(`Devis ${result.external_id} créé avec succès !`);
    } catch (err) {
      alert(err?.message || "Erreur lors de la génération du devis");
    } finally {
      setGeneratingQuoteId(null);
    }
  }

  async function handleQuickStatusChange(prestation, nextStatus) {
    if (!prestation?.id || !nextStatus || prestation.status === nextStatus) return;
    setChangingStatusId(prestation.id);
    try {
      await changePrestationStatus(prestation.id, nextStatus, null);
      setDetailsById((prev) => {
        const next = { ...prev };
        delete next[prestation.id];
        return next;
      });
      await loadData();
    } catch (err) {
      alert(err?.message || "Erreur changement statut");
    } finally {
      setChangingStatusId(null);
    }
  }

  const totalTtc = useMemo(() => {
    const ht = parseFloat(formData.amount_ht) || 0;
    const tva = parseTvaPct(formData.tva_pct, 18);
    return ht * (1 + tva / 100);
  }, [formData.amount_ht, formData.tva_pct]);

  const totalTtcRounded = useMemo(() => Math.round(totalTtc || 0), [totalTtc]);

  function ensureInstallments() {
    const count = Math.max(2, parseInt(installmentCount || 0, 10) || 2);
    const start =
      installmentStartDate ||
      formData.due_date ||
      formData.service_date ||
      new Date().toISOString().slice(0, 10);

    if (!installmentStartDate) {
      setInstallmentStartDate(start);
    }

    if (!Number.isFinite(totalTtcRounded) || totalTtcRounded <= 0) {
      setInstallments([]);
      return;
    }

    setInstallments(buildInstallments(totalTtcRounded, count, start, installmentFrequency));
  }

  useEffect(() => {
    if (formData.payment_mode !== "INSTALLMENTS") return;
    setAutoSplit(true);
  }, [formData.payment_mode]);

  useEffect(() => {
    if (formData.payment_mode !== "INSTALLMENTS") return;
    if (!autoSplit) return;
    ensureInstallments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formData.payment_mode,
    formData.amount_ht,
    formData.tva_pct,
    installmentCount,
    installmentFrequency,
    installmentStartDate,
    autoSplit,
  ]);

  const installmentsTotal = useMemo(() => {
    return installments.reduce((sum, inst) => sum + Number(inst.amount || 0), 0);
  }, [installments]);

  useEffect(() => {
    if (formData.payment_mode !== "INSTALLMENTS") {
      setInstallmentsError("");
      return;
    }
    if (!installments.length) {
      setInstallmentsError("");
      return;
    }
    if (installmentsTotal !== totalTtcRounded) {
      setInstallmentsError(
        `La somme des tranches (${installmentsTotal}) doit être égale au total TTC (${totalTtcRounded}).`
      );
      return;
    }
    setInstallmentsError("");
  }, [installments, installmentsTotal, totalTtcRounded, formData.payment_mode]);

  function updateInstallment(idx, patch) {
    setAutoSplit(false);
    setInstallments((prev) =>
      prev.map((it, i) => (i === idx ? { ...it, ...patch } : it))
    );
  }

  async function handlePaymentSubmit(e) {
    e.preventDefault();
    if (!paymentTarget || !paymentForm.amount) return;

    setSavingPayment(true);
    try {
      await recordDirectPayment(paymentTarget.id, {
        amount: parseFloat(paymentForm.amount),
        method: paymentForm.method,
        provider: paymentForm.provider || null,
        reference: paymentForm.reference || null,
        notes: paymentForm.notes || null,
        paid_at: paymentForm.paid_at || null,
      });
      setShowPaymentModal(false);
      setPaymentTarget(null);
      setDetailsById((prev) => {
        const next = { ...prev };
        delete next[paymentTarget.id];
        return next;
      });
      await loadData();
    } catch (err) {
      alert(err?.message || "Erreur paiement");
    } finally {
      setSavingPayment(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!formData.title?.trim() || !formData.amount_ht) return;

    setSaving(true);
    try {
      let customerId = formData.customer_id;

      if (!customerId) {
        if (!customerName.trim() || !customerPhone.trim()) {
          alert("Nom et téléphone client requis");
          setSaving(false);
          return;
        }

        const created = await createCustomerAPI({
          name: customerName.trim(),
          phone: customerPhone.trim(),
          email: customerEmail?.trim() || null,
          address: customerAddress?.trim() || null,
        });

        customerId = created?.id || "";
      }

      if (!customerId) {
        alert("Client invalide");
        setSaving(false);
        return;
      }

      const payload = {
        ...formData,
        customer_id: customerId,
        amount_ht: parseFloat(formData.amount_ht) || 0,
        tva_pct: parseTvaPct(formData.tva_pct, 18),
        amount:
          formData.payment_mode === "INSTALLMENTS"
            ? totalTtcRounded
            : totalTtc,
      };
      if (editingPrestation) {
        await updatePrestation(editingPrestation.id, payload);
      } else {
        const created = await createPrestation(payload);

        if (formData.payment_mode === "INSTALLMENTS") {
          const cleaned = (installments || []).map((inst) => ({
            amount: parseMoneyToInt(inst.amount),
            due_date: inst.due_date,
          }));

          if (!cleaned.length) {
            throw new Error("Échéances requises");
          }

          const invalid = cleaned.some(
            (inst) =>
              !inst.due_date ||
              !Number.isFinite(inst.amount) ||
              inst.amount <= 0
          );
          if (invalid) {
            throw new Error("Chaque échéance doit avoir un montant et une date");
          }

          const sum = cleaned.reduce((s, i) => s + i.amount, 0);
          if (sum !== totalTtcRounded) {
            throw new Error("La somme des tranches doit égaler le total TTC");
          }

          await createPaymentPlan(created.id, cleaned);
        }
      }
      setShowModal(false);
      setEditingPrestation(null);
      resetForm();
      await loadData();
    } catch (err) {
      alert(err?.message || "Erreur");
    } finally {
      setSaving(false);
    }
  }

  // ── Handlers Projets ──
  const projectStatusConfig = {
    DRAFT: { label: "Brouillon", tone: "neutral", pct: 20 },
    ACTIVE: { label: "Actif", tone: "green", pct: 60 },
    ON_HOLD: { label: "En pause", tone: "yellow", pct: 45 },
    DONE: { label: "Terminé", tone: "blue", pct: 100 },
    CANCELLED: { label: "Annulé", tone: "red", pct: 0 },
  };

  function resetProjectForm() {
    setProjectFormData({ customer_id: "", title: "", description: "", status: "DRAFT", start_date: "", end_date: "" });
  }

  function openCreateProject() {
    resetProjectForm();
    setEditingProject(null);
    setShowProjectModal(true);
  }

  function openEditProject(project) {
    setEditingProject(project);
    setProjectFormData({
      customer_id: project.customer_id,
      title: project.title || "",
      description: project.description || "",
      status: project.status || "DRAFT",
      start_date: clampDate(project.start_date),
      end_date: clampDate(project.end_date),
    });
    setShowProjectModal(true);
  }

  async function handleProjectSubmit(e) {
    e.preventDefault();
    if (!projectFormData.customer_id || !projectFormData.title?.trim()) return;
    setProjectSaving(true);
    try {
      if (editingProject) {
        await updateProject(editingProject.id, projectFormData);
      } else {
        await createProject(projectFormData);
      }
      setShowProjectModal(false);
      setEditingProject(null);
      resetProjectForm();
      const data = await fetchProjects({});
      setProjects(Array.isArray(data) ? data : []);
    } catch (err) {
      alert(err?.message || "Erreur");
    } finally {
      setProjectSaving(false);
    }
  }

  async function handleDeleteProject(project) {
    if (!window.confirm(`Supprimer le projet "${project.title}" ?`)) return;
    setDeletingProjectId(project.id);
    try {
      await deleteProject(project.id);
      const data = await fetchProjects({});
      setProjects(Array.isArray(data) ? data : []);
    } catch (err) {
      const msg = err?.message || String(err);
      if (msg.includes("project_has_active_prestations")) {
        alert("Impossible de supprimer : des prestations actives sont liées à ce projet.");
      } else {
        alert(msg || "Erreur lors de la suppression");
      }
    } finally {
      setDeletingProjectId(null);
    }
  }

  const computed = useMemo(() => {
    if (kpis) {
      return {
        total: Number(kpis.count_total || 0),
        pending: Number(kpis.count_pending || 0),
        paid: Number(kpis.count_paid || 0),
        partial: Number(kpis.count_partial || 0),
        overdue: Number(kpis.count_overdue || 0),
        totalFacture: Number(kpis.total_facture || 0),
        totalEncaisse: Number(kpis.total_encaisse || 0),
        totalResteDu: Number(kpis.total_reste_du || 0),
        amountOverdue: Number(kpis.amount_overdue || 0),
      };
    }
    const total = prestations.length;
    const pending = prestations.filter((p) => p.status === "PENDING").length;
    const paid = prestations.filter((p) => p.status === "PAID").length;
    const partial = prestations.filter((p) => p.status === "PARTIALLY_PAID").length;
    const overdue = prestations.filter((p) => p.is_overdue).length;
    const totalFacture = prestations.reduce((sum, p) => sum + Number(p.amount_ttc || p.amount || 0), 0);
    const totalEncaisse = prestations.reduce((sum, p) => sum + Number(p.paid_total || 0), 0);
    const totalResteDu = prestations.reduce((sum, p) => sum + Number(p.due_total || 0), 0);
    return { total, pending, paid, partial, overdue, totalFacture, totalEncaisse, totalResteDu, amountOverdue: 0 };
  }, [prestations, kpis]);

  const sortedPrestations = useMemo(() => prestations, [prestations]);

  const projectComputed = useMemo(() => {
    const total = projects.length;
    const active = projects.filter((p) => p.status === "ACTIVE").length;
    const done = projects.filter((p) => p.status === "DONE").length;
    const revenue = projects.reduce((sum, p) => sum + Number(p.total_amount_calc || 0), 0);
    return { total, active, done, revenue };
  }, [projects]);

  const sortedProjects = useMemo(() => {
    let list = [...projects];
    if (projectFilters.search) {
      const q = projectFilters.search.toLowerCase();
      list = list.filter(
        (p) =>
          (p.title || "").toLowerCase().includes(q) ||
          (p.code || "").toLowerCase().includes(q) ||
          (p.customer_name || "").toLowerCase().includes(q)
      );
    }
    if (projectFilters.status) {
      list = list.filter((p) => p.status === projectFilters.status);
    }
    const s = projectFilters.sort;
    if (s === "amount_desc") list.sort((a, b) => Number(b.total_amount_calc || 0) - Number(a.total_amount_calc || 0));
    else if (s === "amount_asc") list.sort((a, b) => Number(a.total_amount_calc || 0) - Number(b.total_amount_calc || 0));
    else if (s === "title_asc") list.sort((a, b) => (a.title || "").localeCompare(b.title || ""));
    else if (s === "title_desc") list.sort((a, b) => (b.title || "").localeCompare(a.title || ""));
    return list;
  }, [projects, projectFilters]);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top header */}
      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-6 py-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Briefcase className="h-4 w-4" />
                <span>Gestion</span>
                <span className="text-slate-300">/</span>
                <span className="text-slate-900 font-medium">
                  {activeTab === "prestations" ? "Prestations" : "Projets"}
                </span>
              </div>
              <h1 className="mt-2 text-2xl font-black tracking-tight text-slate-900">
                Prestations & Projets
              </h1>
              <p className="mt-1 text-sm text-slate-600">
                {activeTab === "prestations"
                  ? "Consultations, services avec paiement unique ou échelonné."
                  : "Regroupez vos prestations par projet client."}
              </p>
            </div>

            <div className="flex items-center gap-3">
              {activeTab === "prestations" ? (
                <button
                  onClick={openCreate}
                  className="inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-slate-800"
                >
                  <Plus className="h-4 w-4" />
                  Nouvelle prestation
                </button>
              ) : (
                <button
                  onClick={openCreateProject}
                  className="inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-slate-800"
                >
                  <Plus className="h-4 w-4" />
                  Nouveau projet
                </button>
              )}
            </div>
          </div>

          {/* Onglets */}
          <div className="mt-5 flex gap-1 border-b border-slate-200">
            {[
              { key: "prestations", label: "Prestations", icon: Briefcase },
              { key: "projets", label: "Projets", icon: FolderKanban },
            ].map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={
                  "inline-flex items-center gap-2 rounded-t-xl px-4 py-2.5 text-sm font-semibold transition-colors -mb-px " +
                  (activeTab === key
                    ? "border border-b-white border-slate-200 bg-white text-slate-900"
                    : "text-slate-500 hover:text-slate-700")
                }
              >
                <Icon className="h-4 w-4" />
                {label}
              </button>
            ))}
          </div>

          {/* KPI row */}
          {activeTab === "prestations" ? (
            <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
              <KpiCard
                icon={DollarSign}
                label="Total facturé TTC"
                value={formatMoneyXOF(computed.totalFacture)}
                sub={`${computed.total} prestations`}
              />
              <KpiCard
                icon={CheckCircle2}
                label="Encaissé"
                value={formatMoneyXOF(computed.totalEncaisse)}
                sub={`${computed.paid} payées, ${computed.partial || 0} partielles`}
              />
              <KpiCard
                icon={AlertTriangle}
                label="Reste dû"
                value={formatMoneyXOF(computed.totalResteDu)}
                sub={`${computed.pending} en attente`}
              />
              <KpiCard
                icon={AlertTriangle}
                label="En retard"
                value={formatMoneyXOF(computed.amountOverdue || 0)}
                sub={`${computed.overdue || 0} prestations`}
              />
            </div>
          ) : (
            <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
              <KpiCard icon={FolderKanban} label="Total projets" value={projectComputed.total} sub="Tous statuts" />
              <KpiCard icon={Users} label="Actifs" value={projectComputed.active} sub="En cours" />
              <KpiCard icon={Calendar} label="Terminés" value={projectComputed.done} sub="Livrés" />
              <KpiCard icon={DollarSign} label="Montant total" value={formatMoneyXOF(projectComputed.revenue)} sub="Somme estimée" />
            </div>
          )}

          {/* Filters */}
          {activeTab === "prestations" ? (
            <div className="mt-6 flex flex-col gap-3 md:flex-row md:items-center">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  value={searchDraft}
                  onChange={(e) => setSearchDraft(e.target.value)}
                  placeholder="Rechercher une prestation, un code, un client…"
                  className="w-full rounded-2xl border border-slate-200 bg-white px-10 py-2.5 text-sm text-slate-900 outline-none ring-0 placeholder:text-slate-400 focus:border-slate-300 focus:ring-4 focus:ring-slate-100"
                />
              </div>
              <div className="flex items-center gap-3">
                <div className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2.5">
                  <SlidersHorizontal className="h-4 w-4 text-slate-500" />
                  <select
                    value={filters.status}
                    onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
                    className="bg-transparent text-sm text-slate-900 outline-none"
                  >
                    <option value="">Tous les statuts</option>
                    {Object.entries(statusConfig).map(([key, val]) => (
                      <option key={key} value={key}>{val.label}</option>
                    ))}
                  </select>
                </div>
                <div className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2.5">
                  <select
                    value={filters.paymentMode}
                    onChange={(e) => setFilters((f) => ({ ...f, paymentMode: e.target.value }))}
                    className="bg-transparent text-sm text-slate-900 outline-none"
                  >
                    <option value="">Tous les paiements</option>
                    {Object.entries(paymentModeConfig).map(([key, val]) => (
                      <option key={key} value={key}>{val.label}</option>
                    ))}
                  </select>
                </div>
                <div className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2.5">
                  <select
                    value={`${filters.sortBy}_${filters.sortOrder}`}
                    onChange={(e) => {
                      const [sortBy, sortOrder] = e.target.value.split("_");
                      setFilters((f) => ({ ...f, sortBy, sortOrder }));
                    }}
                    className="bg-transparent text-sm text-slate-900 outline-none"
                    title="Trier"
                  >
                    <option value="created_at_desc">Tri: récent</option>
                    <option value="due_date_asc">Échéance: proche</option>
                    <option value="amount_ttc_desc">Montant: décroissant</option>
                    <option value="amount_ttc_asc">Montant: croissant</option>
                  </select>
                </div>
                <button
                  onClick={() => setFilters((f) => ({ ...f, overdue: !f.overdue }))}
                  className={
                    "inline-flex items-center gap-2 rounded-2xl border px-3 py-2.5 text-sm font-medium transition " +
                    (filters.overdue
                      ? "border-rose-200 bg-rose-50 text-rose-700"
                      : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50")
                  }
                >
                  <AlertTriangle className="h-4 w-4" />
                  En retard
                </button>
              </div>
            </div>
          ) : (
            <div className="mt-6 flex flex-col gap-3 md:flex-row md:items-center">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  value={projectSearchDraft}
                  onChange={(e) => setProjectSearchDraft(e.target.value)}
                  placeholder="Rechercher un projet, un code, un client…"
                  className="w-full rounded-2xl border border-slate-200 bg-white px-10 py-2.5 text-sm text-slate-900 outline-none ring-0 placeholder:text-slate-400 focus:border-slate-300 focus:ring-4 focus:ring-slate-100"
                />
              </div>
              <div className="flex items-center gap-3">
                <div className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2.5">
                  <SlidersHorizontal className="h-4 w-4 text-slate-500" />
                  <select
                    value={projectFilters.status}
                    onChange={(e) => setProjectFilters((f) => ({ ...f, status: e.target.value }))}
                    className="bg-transparent text-sm text-slate-900 outline-none"
                  >
                    <option value="">Tous les statuts</option>
                    {Object.entries(projectStatusConfig).map(([key, val]) => (
                      <option key={key} value={key}>{val.label}</option>
                    ))}
                  </select>
                </div>
                <div className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2.5">
                  <select
                    value={projectFilters.sort}
                    onChange={(e) => setProjectFilters((f) => ({ ...f, sort: e.target.value }))}
                    className="bg-transparent text-sm text-slate-900 outline-none"
                    title="Trier"
                  >
                    <option value="updated_desc">Tri: récent</option>
                    <option value="amount_desc">Montant: décroissant</option>
                    <option value="amount_asc">Montant: croissant</option>
                    <option value="title_asc">Titre: A → Z</option>
                    <option value="title_desc">Titre: Z → A</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Content — Projets */}
      {activeTab === "projets" && (
        <div className="mx-auto max-w-7xl px-6 py-6">
          {loading ? (
            <SkeletonGrid />
          ) : sortedProjects.length === 0 ? (
            <div className="rounded-3xl border border-slate-200 bg-white p-10 text-center shadow-sm">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl border border-slate-200 bg-slate-50">
                <FolderKanban className="h-7 w-7 text-slate-600" />
              </div>
              <h3 className="mt-4 text-lg font-bold text-slate-900">Aucun projet trouvé</h3>
              <p className="mt-1 text-sm text-slate-600">Essaie un autre filtre, ou crée ton premier projet.</p>
              <button
                onClick={openCreateProject}
                className="mt-5 inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800"
              >
                <Plus className="h-4 w-4" />
                Créer un projet
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              {sortedProjects.map((project) => (
                <ProjectCard
                  key={project.id}
                  project={project}
                  projectStatusConfig={projectStatusConfig}
                  deletingProjectId={deletingProjectId}
                  onEdit={openEditProject}
                  onDelete={handleDeleteProject}
                  onViewPrestations={() => {
                    setActiveTab("prestations");
                    setFilters((f) => ({ ...f, search: "" }));
                  }}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Content — Prestations */}
      {activeTab === "prestations" && (
        <div className="mx-auto max-w-7xl px-6 py-6">
          {loading ? (
            <SkeletonGrid />
          ) : sortedPrestations.length === 0 ? (
            <div className="rounded-3xl border border-slate-200 bg-white p-10 text-center shadow-sm">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl border border-slate-200 bg-slate-50">
                <Briefcase className="h-7 w-7 text-slate-600" />
              </div>
              <h3 className="mt-4 text-lg font-bold text-slate-900">Aucune prestation trouvée</h3>
              <p className="mt-1 text-sm text-slate-600">
                Essaie un autre filtre, ou crée ta première prestation.
              </p>
              <button
                onClick={openCreate}
                className="mt-5 inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800"
              >
                <Plus className="h-4 w-4" />
                Créer une prestation
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              {sortedPrestations.map((prestation) => (
                <PrestationCard
                  key={prestation.id}
                  prestation={prestation}
                  statusConfig={statusConfig}
                  paymentModeConfig={paymentModeConfig}
                  openDetailId={openDetailId}
                  setOpenDetailId={setOpenDetailId}
                  detailsById={detailsById}
                  loadingDetailId={loadingDetailId}
                  changingStatusId={changingStatusId}
                  generatingQuoteId={generatingQuoteId}
                  onEdit={openEdit}
                  onPay={openPaymentModal}
                  onQuickStatus={handleQuickStatusChange}
                  onGenerateQuote={handleGenerateQuote}
                  onToggleDetails={toggleDetails}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Modal Prestation */}
      <PrestationFormModal
        show={showModal}
        onClose={() => { setShowModal(false); setEditingPrestation(null); }}
        editingPrestation={editingPrestation}
        formData={formData}
        setFormData={setFormData}
        customerSearch={customerSearch}
        setCustomerSearch={setCustomerSearch}
        pickedCustomer={pickedCustomer}
        setPickedCustomer={setPickedCustomer}
        customerName={customerName}
        setCustomerName={setCustomerName}
        customerPhone={customerPhone}
        setCustomerPhone={setCustomerPhone}
        customerEmail={customerEmail}
        setCustomerEmail={setCustomerEmail}
        customerAddress={customerAddress}
        setCustomerAddress={setCustomerAddress}
        customers={customers}
        projects={projects}
        installmentCount={installmentCount}
        setInstallmentCount={setInstallmentCount}
        installmentFrequency={installmentFrequency}
        setInstallmentFrequency={setInstallmentFrequency}
        installmentStartDate={installmentStartDate}
        setInstallmentStartDate={setInstallmentStartDate}
        installments={installments}
        installmentsError={installmentsError}
        totalTtcRounded={totalTtcRounded}
        saving={saving}
        onSubmit={handleSubmit}
        statusConfig={statusConfig}
        paymentModeConfig={paymentModeConfig}
        ensureInstallments={ensureInstallments}
        updateInstallment={updateInstallment}
        setAutoSplit={setAutoSplit}
      />

      {/* Modal Paiement */}
      <PaymentModal
        show={showPaymentModal}
        onClose={() => { setShowPaymentModal(false); setPaymentTarget(null); }}
        paymentTarget={paymentTarget}
        paymentForm={paymentForm}
        setPaymentForm={setPaymentForm}
        savingPayment={savingPayment}
        onSubmit={handlePaymentSubmit}
      />

      {/* Modal Projet */}
      <ProjectFormModal
        show={showProjectModal}
        onClose={() => { setShowProjectModal(false); setEditingProject(null); }}
        editingProject={editingProject}
        projectFormData={projectFormData}
        setProjectFormData={setProjectFormData}
        customers={customers}
        projectStatusConfig={projectStatusConfig}
        saving={projectSaving}
        onSubmit={handleProjectSubmit}
      />
    </div>
  );
}
